import { useState, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Coins, Loader2, Leaf, Crown, Check } from "lucide-react";
import { Sparkle } from "@/components/icons";
import Navbar from "@/components/Navbar";
import BottomNav from "@/components/BottomNav";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const POPULAR = "credits_3000";

export default function Pricing() {
  const { user, refreshUser } = useAuth();
  const [packages, setPackages] = useState([]);
  const [coinPacks, setCoinPacks] = useState([]);
  const [busy, setBusy] = useState("");
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [checking, setChecking] = useState(false);

  useEffect(() => { api.get("/billing/packages").then((r) => setPackages(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/coins/packages").then((r) => setCoinPacks(r.data)).catch(() => {}); }, []);

  const poll = useCallback(async (sessionId, attempts = 0) => {
    if (attempts > 6) { setChecking(false); toast.error("Payment status timed out. Check back shortly."); return; }
    try {
      const { data } = await api.get(`/billing/status/${sessionId}`);
      if (data.payment_status === "paid") {
        setChecking(false);
        await refreshUser();
        toast.success("Payment successful — credits added! 🎬");
        params.delete("session_id"); setParams(params, { replace: true });
        return;
      }
      if (data.status === "expired") { setChecking(false); toast.error("Payment session expired."); return; }
      setTimeout(() => poll(sessionId, attempts + 1), 2000);
    } catch (e) {
      setChecking(false);
      toast.error("Could not verify payment.");
    }
  }, [params, setParams, refreshUser]);

  useEffect(() => {
    const sid = params.get("session_id");
    if (sid) { setChecking(true); poll(sid); }
  }, [params, poll]);

  const pollMembership = useCallback(async (sessionId, attempts = 0) => {
    if (attempts > 8) { setChecking(false); return; }
    try {
      const { data } = await api.get(`/membership/status/${sessionId}`);
      if (data.active && data.tier && data.tier !== "trial") {
        setChecking(false); await refreshUser();
        toast.success(`${data.tier === "pro" ? "Pro" : "Basic"} plan active 🎉`);
        params.delete("membership_session"); setParams(params, { replace: true }); return;
      }
      setTimeout(() => pollMembership(sessionId, attempts + 1), 2000);
    } catch { setChecking(false); }
  }, [params, setParams, refreshUser]);

  useEffect(() => {
    const ms = params.get("membership_session");
    if (ms) { setChecking(true); pollMembership(ms); }
  }, [params, pollMembership]);

  const choosePlan = async (tier) => {
    setBusy(tier);
    try {
      const { data } = await api.post("/membership/checkout", { tier, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Checkout failed"); setBusy(""); }
  };

  const m = user?.membership || {};
  const curTier = user?.tier;

  const buy = async (pkgId) => {
    setBusy(pkgId);
    try {
      const { data } = await api.post("/billing/checkout", { package_id: pkgId, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Checkout failed");
      setBusy("");
    }
  };

  const buyCoins = async (packId) => {
    setBusy(packId);
    try {
      const { data } = await api.post("/coins/checkout", { pack_id: packId, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Checkout failed");
      setBusy("");
    }
  };

  const packs = packages.filter((p) => p.type === "pack");

  return (
    <div className="min-h-screen bg-ink-950 text-[#F0E9DD] pb-24">
      <Navbar />
      {checking && (
        <div className="fixed inset-0 z-[60] bg-ink-950/80 backdrop-blur flex items-center justify-center" data-testid="payment-checking">
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="animate-spin text-gold" size={36} />
            <p className="label-mono">Verifying payment…</p>
          </div>
        </div>
      )}
      <main className="max-w-[1200px] mx-auto px-5 sm:px-8 py-12">
        {/* Plans */}
        <div className="text-center mb-8" data-testid="plans-section">
          <div className="label-mono mb-2">Membership</div>
          <h1 className="font-heading font-black uppercase tracking-tighter text-4xl sm:text-5xl">Choose your plan</h1>
          <p className="text-[#A89F94] mt-3 max-w-xl mx-auto text-sm">
            {m.status === "trial" ? `You're on the free trial — ${m.days_left} days left.`
              : m.status === "active" ? `Your ${curTier === "pro" ? "Pro" : "Basic"} plan is active.`
              : m.status === "grace" ? `Your plan has ended. Pick a plan within ${m.days_left} days to keep your account.`
              : "Pick a plan to unlock posting, messaging and more."}
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto mb-16">
          <div className={`rounded-2xl border p-6 bg-ink-900 ${curTier === "basic" ? "border-green-500" : "border-zinc-800"}`} data-testid="plan-card-basic">
            <div className="flex items-center gap-2 mb-1"><Leaf className="text-green-500" size={24} /><span className="font-heading font-black text-xl">Basic</span>
              {curTier === "basic" && <span className="ml-auto text-[10px] font-bold text-green-400 border border-green-500/50 rounded-full px-2 py-0.5 uppercase">Current</span>}</div>
            <div className="mt-3 mb-4"><span className="font-heading font-black text-4xl">$0.99</span><span className="text-[#A89F94] text-sm"> per year</span></div>
            <ul className="space-y-1.5 mb-5">{["Create & customize your page", "Upload posts (images/videos)", "Use AI or Not", "Community feed", "Basic support"].map((f) => <li key={f} className="flex items-center gap-2 text-sm text-zinc-300"><Check size={14} className="text-blue-400 shrink-0" /> {f}</li>)}</ul>
            <button onClick={() => choosePlan("basic")} disabled={busy === "basic"} data-testid="choose-basic" className="w-full bg-blue-600 text-white font-heading font-bold py-3 rounded-lg hover:bg-blue-500 transition-colors disabled:opacity-50">{busy === "basic" ? "Redirecting…" : curTier === "basic" ? "Renew Basic" : "Select Basic"}</button>
          </div>
          <div className="relative rounded-2xl border border-green-500 p-6 bg-ink-900 shadow-[0_0_30px_-8px_rgba(34,197,94,0.5)]" data-testid="plan-card-pro">
            <div className="absolute -top-3 right-6 bg-green-500 text-ink-950 text-xs font-heading font-bold px-2.5 py-1 rounded-full uppercase">Most Popular</div>
            <div className="flex items-center gap-2 mb-1"><Crown className="text-green-500" size={24} /><span className="font-heading font-black text-xl">Pro</span>
              {curTier === "pro" && <span className="ml-auto text-[10px] font-bold text-green-400 border border-green-500/50 rounded-full px-2 py-0.5 uppercase">Current</span>}</div>
            <div className="mt-3 mb-4"><span className="font-heading font-black text-4xl">$0.99</span><span className="text-[#A89F94] text-sm"> per month</span></div>
            <ul className="space-y-1.5 mb-5">{["Everything in Basic", "Send invoices", "Direct messaging earning ($0–$30)", "Access new features first", "Priority support"].map((f) => <li key={f} className="flex items-center gap-2 text-sm text-zinc-300"><Check size={14} className="text-blue-400 shrink-0" /> {f}</li>)}</ul>
            <button onClick={() => choosePlan("pro")} disabled={busy === "pro"} data-testid="choose-pro" className="w-full bg-green-500 text-ink-950 font-heading font-bold py-3 rounded-lg hover:bg-green-400 transition-colors disabled:opacity-50">{busy === "pro" ? "Redirecting…" : curTier === "pro" ? "Renew Pro" : "Select Pro"}</button>
          </div>
        </div>

        <div className="text-center mb-4">
          <div className="label-mono mb-2">Upgrade</div>
          <h1 className="font-heading font-black uppercase tracking-tighter text-4xl sm:text-5xl">Nosy AI Credits</h1>
          <p className="text-[#A89F94] mt-4 max-w-xl mx-auto">Buy credits once — they never expire. Spend them to generate AI images and animations. Storyboards, edits and scripts stay free.</p>
          <div className="inline-flex items-center gap-2 mt-6 border border-gold/40 bg-[#1a140d] px-4 py-2 rounded-full">
            <Sparkle size={15} className="text-gold" />
            <span className="font-mono text-sm">Your credits: <span className="text-gold font-bold" data-testid="pricing-balance">{user?.role === "admin" ? "∞" : (user?.credits ?? 0)}</span></span>
          </div>
        </div>

        {/* credit packs */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-10">
          {packs.map((p, i) => (
            <motion.div key={p.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
              className={`relative border bg-ink-900 p-6 flex flex-col rounded-2xl ${p.id === POPULAR ? "border-gold" : "border-zinc-800"}`} data-testid={`pack-${p.id}`}>
              {p.id === POPULAR && <div className="absolute -top-3 left-6 bg-gold text-ink-950 text-xs font-heading font-bold px-2.5 py-1 rounded flex items-center gap-1"><Sparkle size={12} /> BEST VALUE</div>}
              <div className="flex items-center gap-1.5 font-heading font-black text-3xl"><Sparkle size={20} className="text-gold" />{p.credits.toLocaleString()}</div>
              <div className="label-mono text-zinc-500 mt-1">Nosy AI credits</div>
              <div className="mt-4 mb-1"><span className="font-heading font-black text-4xl">${p.amount}</span><span className="text-[#A89F94] text-sm"> one-time</span></div>
              <div className="text-xs text-zinc-500 mb-4">≈ {p.images?.toLocaleString()} images · {p.clips?.toLocaleString()} animations</div>
              <p className="text-sm text-[#A89F94] flex-1">{p.description}</p>
              <button onClick={() => buy(p.id)} disabled={busy === p.id} data-testid={`buy-${p.id}`}
                className={`mt-6 rounded-lg font-heading font-bold py-3 transition-colors disabled:opacity-60 ${p.id === POPULAR ? "bg-gold text-ink-950 hover:bg-goldlight" : "bg-ink-800 border border-zinc-700 text-[#F0E9DD] hover:border-gold"}`}>
                {busy === p.id ? "Redirecting…" : `Buy for $${p.amount}`}
              </button>
            </motion.div>
          ))}
        </div>

        {/* pricing chart */}
        <div className="mt-12 border border-zinc-800 bg-ink-900 rounded-2xl overflow-hidden" data-testid="credit-chart">
          <div className="px-5 py-4 border-b border-zinc-800 label-mono">Credit packages at a glance</div>
          <div className="grid grid-cols-[1fr_auto] sm:grid-cols-[1fr_auto_auto] gap-x-4 text-sm">
            <div className="hidden sm:contents text-zinc-500 label-mono">
              <div className="px-5 py-3 border-b border-white/5">Credits</div>
              <div className="px-5 py-3 border-b border-white/5 text-right">Nosy AI price</div>
              <div className="px-5 py-3 border-b border-white/5">What you can make</div>
            </div>
            {packs.map((p) => (
              <div key={p.id} className="contents">
                <div className="px-5 py-4 border-b border-white/5 flex items-center gap-2 font-heading font-bold"><Sparkle size={15} className="text-gold" />{p.credits.toLocaleString()} credits</div>
                <div className="px-5 py-4 border-b border-white/5 text-right font-heading font-black text-gold">${p.amount}</div>
                <div className="px-5 py-4 border-b border-white/5 text-[#A89F94] col-span-2 sm:col-span-1">≈ {p.images?.toLocaleString()} AI images or {p.clips?.toLocaleString()} short animations</div>
              </div>
            ))}
          </div>
          <p className="px-5 py-3 text-xs text-zinc-600">Credits fuel image & video generation (~5 credits per image, ~25 per animation clip). Prices include a small Nosy AI service fee over provider cost.</p>
        </div>

        {/* buy coins (social currency) */}
        <div className="mt-14" data-testid="buy-coins-section">
          <div className="flex items-center gap-2 mb-1">
            <Coins size={22} className="text-yellow-400" />
            <h2 className="font-heading font-black uppercase tracking-tight text-2xl">Buy Coins</h2>
          </div>
          <p className="text-[#A89F94] text-sm mb-4">Coins power the social side — tip creators, comment, and send paid DMs. <span className="text-yellow-300">70 coins = $1</span>.</p>
          <div className="inline-flex items-center gap-2 mb-6 border border-yellow-500/40 bg-[#1a140d] px-4 py-2 rounded-full">
            <Coins size={16} className="text-yellow-400" />
            <span className="font-mono text-sm">Coins: <span className="text-yellow-300 font-bold" data-testid="pricing-coin-balance">{Math.round(user?.spendable_coins ?? user?.coin_balance ?? 0)}</span></span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {coinPacks.map((p) => (
              <div key={p.id} className="border border-zinc-800 bg-ink-900 p-5 flex flex-col items-start rounded-xl" data-testid={`coin-pack-${p.id}`}>
                <div className="flex items-center gap-1.5 font-heading font-black text-2xl"><Coins size={18} className="text-yellow-400" />{p.coins.toLocaleString()}</div>
                <div className="label-mono text-zinc-500 mt-1 mb-4">{p.label}</div>
                <button onClick={() => buyCoins(p.id)} disabled={busy === p.id} data-testid={`buy-coins-${p.id}`}
                  className="mt-auto w-full bg-yellow-500 text-ink-950 font-heading font-bold px-4 py-2.5 rounded-lg hover:bg-yellow-400 transition-colors disabled:opacity-60">
                  {busy === p.id ? "…" : `$${p.amount}`}
                </button>
              </div>
            ))}
          </div>
        </div>

        <p className="text-center label-mono mt-12">Test mode — use Stripe test card 4242 4242 4242 4242</p>
        <div className="text-center mt-4">
          <button onClick={() => navigate("/home")} className="text-gold text-sm hover:underline" data-testid="back-to-dashboard">← Back to home</button>
        </div>
      </main>
      <BottomNav />
    </div>
  );
}
